import React from "react";

import MuiAlert from "@mui/material/Alert";
import { IconButton, Typography } from "@mui/material";
import { getFormConfig, getFormData } from "./Form/Testing/FormTestData";
import { FormBuilder } from "./Form/FormBuilder";
import styled from "styled-components";
import { FormRuntime } from "./Form/FormRuntime";
import { FormConfiguration } from "./Form/FormConfiguration";

import { FlowBuilder } from "./Flow/FlowBuilder";
import { FlowConfiguration } from "./rule-engine-core/Flow/FlowConfiguration";
import { ExpressionBuilder } from "./Expressions/ExpressionBuilder";
import { ExpressionConfiguration } from "./rule-engine-core/Expressions/ExpressionConfiguration";

import { RuleTableBuilder } from "./Rule/Table/RuleTable/RuleTableBuilder";

import { ValueTableRuleBuilder } from "./Rule/Table/ValueTable/ValueTableRuleBuilder";

import { IntersectionRuleBuilder } from "./Rule/Intersection/IntersectionRuleBuilder";

import { AutoBind } from "./rule-engine-core/Common/AutoBind";

import { NumberDataDefinition } from "./rule-engine-core/Common/Data/Definitions/NumberDataDefinition";
import { DivideExpression } from "./rule-engine-core/Expressions/Expressions/Math/DivideExpression";
import { IfThenExpression, IfThenStyle } from "./rule-engine-core/Expressions/Expressions/Logic/IfThenExpression";

import { TreeRuleBuilder } from "./Rule/Tree/TreeRuleBuilder";

import { StatementRuleBuilder } from "./Rule/Statement/StatementRuleBuilder";

import { TextMergeConfiguration } from "./rule-engine-core/TextMerge/TextMergeConfiguration";
import { TextMergeBuilder } from "./TextMerge/TextMergeBuilder";

import { MappingBuilder } from "./Mapping/MappingBuilder";

import { AddExpression } from "./rule-engine-core/Expressions/Expressions/Math/AddExpression";
import { changePage, MenuCategory, MenuColumn, MenuItem, TestAppMenu } from "./TestAppMenu";

import { getStatementRuleEmpty, getStatementRuleExample } from "./CommonUI/Example/Rule/Statement";
import { getTestInputDataDefinition } from "./CommonUI/Example/DataDefinition";
import { getTreeRuleEmpty, getTreeRuleExample } from "./CommonUI/Example/Rule/Tree";
import { getIntersectionRuleEmpty, getIntersectionRulExample } from "./CommonUI/Example/Rule/Intersection";
import { getValueTableEmpty, getValueTableExample } from "./CommonUI/Example/Rule/Table/ValueTable";
import { getRuleTableEmpty, getRuleTableExample } from "./CommonUI/Example/Rule/Table/RuleTable";
import HomePageTitleBg from "./assets/images/Frame 1760.png";

import WebFormsServiceImg from "./assets/images/Services/WebForms.png";
import FormBuilderEmpty from "./assets/images/Services/Form_builder_empty.png";
import FormEmpty from "./assets/images/Services/form_empty.png";

import FlowServiceImg from "./assets/images/Services/Flow.png";

import TreeRuleServiceImg from "./assets/images/Services/TreeRule.png";
import TreeRuleEmpty from "./assets/images/Services/Tree Rules_empty.png";
import ValueRuleServiceImg from "./assets/images/Services/Value_table.png";
import ValueRuleEmpty from "./assets/images/Services/Value Table_empty.png";
import RuleTableServiceImg from "./assets/images/Services/Rule_table.png";
import RuleTableEmpty from "./assets/images/Services/Rule Table_ empty.png";
import IntersectionRuleServiceImg from "./assets/images/Services/IntersectionRule.png";
import StatementRule from "./assets/images/Services/Statement Rule.png";
import StatementRuleEmpty from "./assets/images/Services/Statement Rule_empty.png";

import IntersectionRuleEmpty from "./assets/images/Services/Intersection Rules_empty.png";

import ExpressionServiceImg from "./assets/images/Services/Expression.png";
import TextMergeImg from "./assets/images/Services/Text Merge.png";
import MappingBuilderImg from "./assets/images/Services/Mapping Builder.png";

import { CloseIcon } from "./CommonUI/Icon/CloseIcon";
import { FormBlueIcon } from "./CommonUI/Icon/FormBlueIcon";
import { StyledButton } from "./CommonUI/BuildlingBlocks/StyledButton";
import { Flow1Icon } from "./CommonUI/Icon/Flow1Icon";
import { RulesGreenIcon } from "./CommonUI/Icon/RulesGreenIcon";
import { ExpressionBlueIcon } from "./CommonUI/Icon/ExpressionBlueIcon";
import { OtherBuilderIcon } from "./CommonUI/Icon/OtherBuilderIcon";
import { ExecutionData } from "./rule-engine-core/Common/Data/ExecutionData/ExecutionData";

import { getMappingEmpty } from "./CommonUI/Example/Mapping";

import "./App.css";
import { StoryView } from "./CommonUI/StoryBook/StoryView";
import { StoryData } from "./CommonUI/StoryBook/StoryData";
import { RegisterStories } from "./CommonUI/StoryBook/RegisterStories";
import { BooleanDataDefinition } from "./rule-engine-core/Common/Data/Definitions/BooleanDataDefinition";
import { StringDataDefinition } from "./rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { UILayerFormHost } from "./Form/Utilities/UILayerFormHost";
import { UIStyleConstants } from "./rule-engine-core/Common/UIStyleConstants";
import { UICollapse } from "./CommonUI/BuildlingBlocks/UICollapse";
import { TextMergeType } from "./rule-engine-core/TextMerge/TextMergeType";
import { OpenAIChatLayer } from "./CommonUI/OpenAI/OpenAIChatLayer";
import { ElectronicsDataDecisioningDemo } from "./Demos/ElectronicsDataDecisioning/ElectronicsDataDecisioningDemo";

class Notification {
    type: "info" | "success" | "error" | "warning" = "error";
    message: string;

    onClose?: () => void;

    constructor(message: string, type?: "info" | "success" | "error" | "warning") {
        this.message = message;

        if (type) {
            this.type = type;
        }
    }
}

const TestHomeItemUI = (props: { item: MenuItem; category: string }) => {
    return (
        <TestHomeItemRender className="TestHomeItem">
            <TestHomeItemImageWrapper className="image-wrapper">{props.item.img()}</TestHomeItemImageWrapper>
            <TestHomeItemActions>
                {props.item.name}{" "}
                <StyledButton
                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                    variant="contained"
                    onClick={() => {
                        changePage(props.category, props.item.name);
                    }}
                >
                    Open
                </StyledButton>
            </TestHomeItemActions>
        </TestHomeItemRender>
    );
};

const TestHomeSectionUI = (props: { category: MenuCategory }) => {
    return (
        <TestHomeItemRender>
            <TestHomeSectionHeader>
                <TestHomeSectionTitle>{props.category.name}</TestHomeSectionTitle>
            </TestHomeSectionHeader>
            {props.category.description && <TestHomeDescription>{props.category.description}</TestHomeDescription>}
            <TestHomeCategoryItemSection>
                {props.category.items.map((item, idx) => (
                    <TestHomeItemUI
                        item={item}
                        key={idx}
                        category={props.category.name}
                    />
                ))}
            </TestHomeCategoryItemSection>
        </TestHomeItemRender>
    );
};

const TestHomeColumnUI = (props: { menuColumn: MenuColumn }) => {
    return (
        <TestHomeColumn>
            {/* <TestHomeColumnTitle color={props.menuColumn.color}>
                {props.menuColumn.icon()} {props.menuColumn.name}
            </TestHomeColumnTitle> */}
            {props.menuColumn.description && (
                <TestHomeColumnDescription color={props.menuColumn.color}>
                    {props.menuColumn.description}
                </TestHomeColumnDescription>
            )}
            {props.menuColumn.categories.map((item, idx) =>
                item instanceof MenuItem ? (
                    <TestHomeItemUI
                        item={item}
                        key={idx}
                        category={props.menuColumn.name}
                    />
                ) : (
                    <TestHomeSectionUI
                        category={item}
                        key={idx}
                    />
                ),
            )}
        </TestHomeColumn>
    );
};

class App extends React.Component<
    {},
    {
        notification: Notification | undefined;
        page?: string;
        category: string;
        chatOpen: boolean;
    }
> {
    page?: string;
    category: string = "Form Builder";
    filledFormConfig: FormConfiguration = getFormConfig();
    filledFormData: ExecutionData = getFormData();
    formConfig: FormConfiguration = new FormConfiguration();
    formData: ExecutionData = new ExecutionData();
    menuItems: MenuColumn[] = [];

    constructor(props: {}) {
        super(props);

        this.state = {
            notification: undefined,
            page: undefined,
            category: "Form Builder",
            chatOpen: false,
        };

        this.registerMenuItems();
        RegisterStories();
    }

    componentDidMount() {
        let params = new URLSearchParams(window.location.search);
        let page = params.get("page") || "";
        let category = params.get("category");

        this.page = page;
        this.category = category || "";

        this.setState({
            page,
            category: this.category,
        });
    }

    @AutoBind
    registerMenuItems() {
        let menuColumn = new MenuColumn(
            "Expressions",
            [
                new MenuItem(
                    "Expression Builder (Logic)",
                    () => {
                        let exp1 = new ExpressionConfiguration();
                        exp1.executionResult = new NumberDataDefinition();

                        let baseExpression = new IfThenExpression();
                        baseExpression.ifThenStyle = IfThenStyle.BRANCH_DATA_RETURN;

                        exp1.expression = baseExpression;

                        exp1.inputData = getTestInputDataDefinition();

                        return <ExpressionBuilder expression={exp1} />;
                    },
                    () => (
                        <img
                            src={ExpressionServiceImg}
                            alt="Expression Builder (Filled - Math)"
                        />
                    ),
                ),

                new MenuItem(
                    "Expression Builder (Filled - Logic)",
                    () => {
                        let exp1 = new ExpressionConfiguration();
                        exp1.executionResult = new NumberDataDefinition();

                        let baseExpression = new IfThenExpression();
                        baseExpression.ifThenStyle = IfThenStyle.BRANCH_DATA_RETURN;

                        baseExpression.ifBranches[0]!.ifExpression!.elements[0]!.variableName = "fullTime";

                        baseExpression.ifBranches[0].thenExpression.value = 6000;
                        let second = baseExpression.addIfBranch();
                        second.ifExpression.elements[0]!.variableName = "isManager";
                        second.thenExpression.value = 0;

                        baseExpression.defaultOutput.value = 5000;
                        exp1.expression = baseExpression;

                        exp1.inputData = getTestInputDataDefinition();

                        return <ExpressionBuilder expression={exp1} />;
                    },
                    () => (
                        <img
                            src={ExpressionServiceImg}
                            alt="Expression Builder (Filled - Math)"
                        />
                    ),
                ),

                new MenuItem(
                    "Expression Builder (Filled - Math)",
                    () => {
                        let expression = new DivideExpression();
                        expression.divideValue.variableName = "vacationDaysUsed";
                        let add = new AddExpression();

                        add.value1.variableName = "vacationDaysUsed";
                        add.value2.variableName = "vacationDaysRemaining";
                        expression.divideBy.subExpression = add;

                        let exp = new ExpressionConfiguration();
                        exp.expression = expression;
                        exp.executionResult = new NumberDataDefinition();
                        exp.inputData = getTestInputDataDefinition();
                        return <ExpressionBuilder expression={exp} />;
                    },
                    () => (
                        <img
                            src={ExpressionServiceImg}
                            alt="Expression Builder (Filled - Math)"
                        />
                    ),
                ),

                new MenuItem(
                    "Expression Builder (Blank - Boolean)",
                    () => {
                        let exp1 = new ExpressionConfiguration();
                        exp1.executionResult = new BooleanDataDefinition();

                        exp1.inputData = getTestInputDataDefinition();

                        return (
                            <ExpressionBuilder
                                expression={exp1}
                                openFirstTimeWhenEmptyExpression={false}
                            />
                        );
                    },
                    () => (
                        <img
                            src={ExpressionServiceImg}
                            alt="Expression Builder (Filled - Math)"
                        />
                    ),
                ),
                new MenuItem(
                    "Expression Builder (Blank - Number)",
                    () => {
                        let exp1 = new ExpressionConfiguration();
                        exp1.executionResult = new NumberDataDefinition();

                        exp1.inputData = getTestInputDataDefinition();

                        return (
                            <ExpressionBuilder
                                expression={exp1}
                                openFirstTimeWhenEmptyExpression={false}
                            />
                        );
                    },
                    () => (
                        <img
                            src={ExpressionServiceImg}
                            alt="Expression Builder (Filled - Math)"
                        />
                    ),
                ),
                new MenuItem(
                    "Expression Builder (Blank - String [choice])",
                    () => {
                        let exp1 = new ExpressionConfiguration();
                        let dd = new StringDataDefinition();
                        dd.validValues = ["Normal", "Rush", "Cancel"];
                        exp1.executionResult = dd;

                        exp1.inputData = getTestInputDataDefinition();

                        return (
                            <ExpressionBuilder
                                expression={exp1}
                                openFirstTimeWhenEmptyExpression={false}
                            />
                        );
                    },
                    () => (
                        <img
                            src={ExpressionServiceImg}
                            alt="Expression Builder (Filled - Math)"
                        />
                    ),
                ),
            ],
            UIStyleConstants.BLUE_COLOR,
            () => <ExpressionBlueIcon />,
            "Create complex logic by combining of formulas and logical elements, focusing on one piece at a time. Because statements can be combined, you are not limited to single expressions or value evaluation at any node.",
        );

        this.menuItems.push(menuColumn);

        menuColumn = new MenuColumn(
            "Rules",
            [
                new MenuCategory(
                    "Rule Table",
                    [
                        new MenuItem(
                            "Blank",
                            () => {
                                return (
                                    <RuleTableBuilder
                                        ruleDefinition={getRuleTableEmpty()}
                                        ruleTableName="Rule Table"
                                        onRuleSave={this.onSave}
                                        onRuleCancel={this.goToHome}
                                    />
                                );
                            },
                            () => (
                                <img
                                    src={RuleTableEmpty}
                                    alt="Blank"
                                />
                            ),
                        ),
                        new MenuItem(
                            "Vacation Approval Policy",
                            () => {
                                return (
                                    <RuleTableBuilder
                                        ruleDefinition={getRuleTableExample()}
                                        ruleTableName="Vacation Approval Policy"
                                        onRuleSave={this.onSave}
                                        onRuleCancel={this.goToHome}
                                    />
                                );
                            },
                            () => (
                                <img
                                    src={RuleTableServiceImg}
                                    alt="Vacation Approval Policy"
                                />
                            ),
                        ),
                    ],
                    "Leverage great flexibility in creating business logic through selected evaluators and conditions in each and every row with this tabular format.",
                ),
                new MenuCategory(
                    "Value Table",
                    [
                        new MenuItem(
                            "Blank",
                            () => {
                                return (
                                    <ValueTableRuleBuilder
                                        ruleDefinition={getValueTableEmpty()}
                                        valueTableName="Value Table Rule"
                                        onRuleSave={this.onSave}
                                        onRuleCancel={this.goToHome}
                                    />
                                );
                            },
                            () => (
                                <img
                                    src={ValueRuleEmpty}
                                    alt="Blank"
                                />
                            ),
                        ),
                        new MenuItem(
                            "Burnout Detection",
                            () => {
                                return (
                                    <ValueTableRuleBuilder
                                        ruleDefinition={getValueTableExample()}
                                        valueTableName="Burnout Detection"
                                        onRuleSave={this.onSave}
                                        onRuleCancel={this.goToHome}
                                    />
                                );
                            },
                            () => (
                                <img
                                    src={ValueRuleServiceImg}
                                    alt="Burnout Detection"
                                />
                            ),
                        ),
                    ],
                    "Visually configure a tabular-style rule that can handle 1000s of different combinations of variables in a succinct manner, with evaluators in the top row of each column.",
                ),
            ],
            UIStyleConstants.GREEN_COLOR,
            () => <RulesGreenIcon />,
            undefined,
        );

        this.menuItems.push(menuColumn);

        menuColumn = new MenuColumn(
            "Rules",
            [
                new MenuCategory(
                    "Intersection Rule",
                    [
                        new MenuItem(
                            "Blank",
                            () => {
                                return (
                                    <IntersectionRuleBuilder
                                        ruleDefinition={getIntersectionRuleEmpty()}
                                        intersectionRuleName="Intersection Rule"
                                        onRuleSave={this.onSave}
                                        onRuleCancel={this.goToHome}
                                    />
                                );
                            },
                            () => (
                                <img
                                    src={IntersectionRuleEmpty}
                                    alt="Blank"
                                />
                            ),
                        ),
                        new MenuItem(
                            "FMLA Pay Requirements",
                            () => {
                                return (
                                    <IntersectionRuleBuilder
                                        ruleDefinition={getIntersectionRulExample()}
                                        intersectionRuleName="FMLA Pay Requirements"
                                        onRuleSave={this.onSave}
                                        onRuleCancel={this.goToHome}
                                    />
                                );
                            },
                            () => (
                                <img
                                    src={IntersectionRuleServiceImg}
                                    alt="FMLA Pay Requirements"
                                />
                            ),
                        ),
                    ],
                    "Easily cross compare two sets of data to reach a desired result with this matrix-style rule.",
                ),
                new MenuCategory(
                    "Tree Rule",
                    [
                        new MenuItem(
                            "Blank",
                            () => {
                                return (
                                    <TreeRuleBuilder
                                        ruleDefinition={getTreeRuleEmpty()}
                                        treeRuleName="Tree Rule"
                                        onRuleSave={this.onSave}
                                        onRuleCancel={this.goToHome}
                                    />
                                );
                            },
                            () => (
                                <img
                                    src={TreeRuleEmpty}
                                    alt="Blank"
                                />
                            ),
                        ),
                        new MenuItem(
                            "Promotion Policy",
                            () => {
                                return (
                                    <TreeRuleBuilder
                                        ruleDefinition={getTreeRuleExample()}
                                        treeRuleName="Promotion Policy"
                                        onRuleSave={this.onSave}
                                        onRuleCancel={this.goToHome}
                                    />
                                );
                            },
                            () => (
                                <img
                                    src={TreeRuleServiceImg}
                                    alt="Promotion Policy"
                                />
                            ),
                        ),
                    ],
                    "Combine the power of the Flow Builder with the Expression Builder to create rules that follow different paths, 100% graphically.",
                ),
                new MenuCategory(
                    "Statement Rule",
                    [
                        new MenuItem(
                            "Blank",
                            () => {
                                const statementRule = getStatementRuleEmpty();

                                return (
                                    <StatementRuleBuilder
                                        ruleDefinition={statementRule}
                                        statementRuleName="Statement Rule"
                                        onRuleSave={this.onSave}
                                        onRuleCancel={this.goToHome}
                                    />
                                );
                            },
                            () => (
                                <img
                                    src={StatementRuleEmpty}
                                    alt="Blank"
                                />
                            ),
                        ),
                        new MenuItem(
                            "Onboarding Validation",
                            () => {
                                const statementRule = getStatementRuleExample();

                                return (
                                    <StatementRuleBuilder
                                        ruleDefinition={statementRule}
                                        statementRuleName="Onboarding Validation"
                                        onRuleSave={this.onSave}
                                        onRuleCancel={this.goToHome}
                                    />
                                );
                            },
                            () => (
                                <img
                                    src={StatementRule}
                                    alt="Onboarding Validation"
                                />
                            ),
                        ),
                    ],
                    "Create if/then rules that can be nested to run logic. Statement rules are foundation rules that are used by other composite rule builders.",
                ),
            ],
            UIStyleConstants.GREEN_COLOR,
            () => <RulesGreenIcon />,
            undefined,
        );

        this.menuItems.push(menuColumn);

        // Form
        menuColumn = new MenuColumn(
            "Forms",
            [
                new MenuItem(
                    "Form Builder (filled)",
                    () => {
                        this.filledFormConfig.inputData = getTestInputDataDefinition();
                        return (
                            <FormBuilder
                                form={this.filledFormConfig}
                                formData={this.filledFormData}
                                formName="Test"
                                onFormSave={this.onSave}
                                onFormClose={this.goToHome}
                            />
                        );
                    },
                    () => (
                        <img
                            src={WebFormsServiceImg}
                            alt="Form Builder (filled)"
                        />
                    ),
                ),
                new MenuItem(
                    "Form Builder",
                    () => {
                        this.formConfig.inputData = getTestInputDataDefinition();
                        return (
                            <FormBuilder
                                form={this.formConfig}
                                formData={this.formData}
                                formName="Test"
                                onFormSave={this.onSave}
                                onFormClose={this.goToHome}
                            />
                        );
                    },
                    () => (
                        <img
                            src={FormBuilderEmpty}
                            alt="Form Builder"
                        />
                    ),
                ),
                new MenuItem(
                    "Form Builder - Empty",
                    () => {
                        return (
                            <FormBuilder
                                form={new FormConfiguration()}
                                formName="Test"
                                onFormSave={this.onSave}
                                onFormClose={this.goToHome}
                            />
                        );
                    },
                    () => (
                        <img
                            src={FormBuilderEmpty}
                            alt="Form Builder"
                        />
                    ),
                ),
                new MenuItem(
                    "Form",
                    () => {
                        let form = getFormConfig();
                        form.inputData = getTestInputDataDefinition();
                        return (
                            <FormWrapper>
                                <FormRuntime form={getFormConfig()}></FormRuntime>
                            </FormWrapper>
                        );
                    },
                    () => (
                        <img
                            src={FormEmpty}
                            alt="Form"
                        />
                    ),
                ),
            ],
            UIStyleConstants.BLUE_GRAY_COLOR,
            () => <FormBlueIcon />,
            "Build responsive forms intuitively with a toolkit of commonly used layouts and components like labels, text fields, checklists, toggle buttons, data grids, and much, much more. Design mobile-optimized forms with the same drag and drop capability, featuring the same robust toolkit as Web Forms.",
        );

        this.menuItems.push(menuColumn);

        menuColumn = new MenuColumn(
            "Workflow",
            [
                new MenuItem(
                    "Flow",
                    () => {
                        let flow = new FlowConfiguration();
                        flow.inputData = getTestInputDataDefinition();
                        return (
                            <FlowBuilder
                                flowName="Empty Flow"
                                flow={flow}
                                onFlowCancel={this.goToHome}
                                onFlowSave={this.onSave}
                            ></FlowBuilder>
                        );
                    },
                    () => (
                        <img
                            src={FlowServiceImg}
                            alt="Flow"
                        />
                    ),
                ),
            ],
            UIStyleConstants.ORANGE_COLOR,
            () => <Flow1Icon />,
            "Link together steps in a 100% graphical environment to automate business processes. These steps leverage the assets created in other Builders such as Forms, Expressions, and Rules.",
        );

        this.menuItems.push(menuColumn);

        menuColumn = new MenuColumn(
            "Demos",
            [
                new MenuItem(
                    "Electronics Data Decisioning",
                    () => <ElectronicsDataDecisioningDemo />,
                    () => (
                        <img
                            src={FlowServiceImg}
                            alt="Electronics Data Decisioning"
                        />
                    ),
                ),
            ],
            UIStyleConstants.BLUE_GRAY_COLOR,
            () => <OtherBuilderIcon />,
            "Customer-ready operating demos that combine generated data, rule evaluation, downstream outputs, and guided walkthroughs.",
        );

        this.menuItems.push(menuColumn);

        menuColumn = new MenuColumn(
            "Other",
            [
                new MenuCategory("Text Merge", [
                    new MenuItem(
                        "Plain",
                        () => {
                            const definition = new TextMergeConfiguration();
                            definition.type = TextMergeType.PlAIN;

                            definition.inputData = getTestInputDataDefinition();

                            return (
                                <TextMergeBuilder
                                    definition={definition}
                                    textMergeName="Merge Editor"
                                    onSave={this.onSave}
                                    onCancel={this.goToHome}
                                />
                            );
                        },
                        () => (
                            <img
                                src={TextMergeImg}
                                alt="Text Merge Editor"
                            />
                        ),
                    ),
                    new MenuItem(
                        "Multi Line",
                        () => {
                            const definition = new TextMergeConfiguration();
                            definition.type = TextMergeType.MULTI_LINE;

                            definition.inputData = getTestInputDataDefinition();

                            return (
                                <TextMergeBuilder
                                    definition={definition}
                                    textMergeName="Merge Editor"
                                    onSave={this.onSave}
                                    onCancel={this.goToHome}
                                />
                            );
                        },
                        () => (
                            <img
                                src={TextMergeImg}
                                alt="Text Merge Editor"
                            />
                        ),
                    ),
                    new MenuItem(
                        "HTML",
                        () => {
                            const definition = new TextMergeConfiguration();
                            definition.type = TextMergeType.HTML;

                            definition.inputData = getTestInputDataDefinition();

                            return (
                                <TextMergeBuilder
                                    definition={definition}
                                    textMergeName="Merge Editor"
                                    onSave={this.onSave}
                                    onCancel={this.goToHome}
                                />
                            );
                        },
                        () => (
                            <img
                                src={TextMergeImg}
                                alt="Text Merge Editor"
                            />
                        ),
                    ),
                    new MenuItem(
                        "JSON",
                        () => {
                            const definition = new TextMergeConfiguration();
                            definition.type = TextMergeType.JSON;

                            definition.inputData = getTestInputDataDefinition();

                            return (
                                <TextMergeBuilder
                                    definition={definition}
                                    textMergeName="Merge Editor"
                                    onSave={this.onSave}
                                    onCancel={this.goToHome}
                                />
                            );
                        },
                        () => (
                            <img
                                src={TextMergeImg}
                                alt="Text Merge Editor"
                            />
                        ),
                    ),
                    new MenuItem(
                        "XML",
                        () => {
                            const definition = new TextMergeConfiguration();
                            definition.type = TextMergeType.XML;

                            definition.inputData = getTestInputDataDefinition();

                            return (
                                <TextMergeBuilder
                                    definition={definition}
                                    textMergeName="Merge Editor"
                                    onSave={this.onSave}
                                    onCancel={this.goToHome}
                                />
                            );
                        },
                        () => (
                            <img
                                src={TextMergeImg}
                                alt="Text Merge Editor"
                            />
                        ),
                    ),
                ]),
                new MenuItem(
                    "Mapping Editor",
                    () => {
                        return (
                            <MappingBuilder
                                mappingBuilderName={"Mapping Builder"}
                                definition={getMappingEmpty()}
                                onSave={this.onSave}
                                onCancel={this.goToHome}
                            />
                        );
                    },
                    () => (
                        <img
                            src={MappingBuilderImg}
                            alt="Mapping Editor"
                        />
                    ),
                ),
                new MenuItem(
                    "Mapping Editor - Filled",
                    () => {
                        let mappingDef = getMappingEmpty();
                        mappingDef.hookDirectConnect("firstName", "fullName");
                        mappingDef.hookDirectConnect("phoneNumber", "phone");
                        return (
                            <MappingBuilder
                                mappingBuilderName={"Mapping Builder"}
                                definition={mappingDef}
                                onSave={this.onSave}
                                onCancel={this.goToHome}
                            />
                        );
                    },
                    () => (
                        <img
                            src={MappingBuilderImg}
                            alt="Mapping Editor"
                        />
                    ),
                ),
                new MenuItem(
                    "UI Examples",
                    () => {
                        return <StoryView node={StoryData.rootNode} />;
                    },
                    () => (
                        <img
                            src={MappingBuilderImg}
                            alt="Mapping Editor"
                        />
                    ),
                ),
            ],
            UIStyleConstants.GRAY_DARK_COLOR,
            () => <OtherBuilderIcon />,
        );

        this.menuItems.push(menuColumn);
    }

    goToHome() {
        window.location.href = "/";
    }

    @AutoBind
    onSave() {
        let nof = new Notification(
            "The AppGlue test app does not save data changes.  Your application can implement its own storage for builder configuration data.",
        );
        nof.onClose = () => {
            this.setState({
                notification: undefined,
            });
        };

        this.setState({
            notification: nof,
        });
    }

    getPage(): string | undefined {
        return this.page;
    }

    getCategory(): string {
        return this.category;
    }

    render() {
        return (
            <TestAppRender>
                <TestAppMenu items={this.menuItems} />
                <UICollapse in={!!this.state.notification}>
                    <MuiAlert
                        severity={this.state.notification?.type}
                        action={
                            this.state.notification?.onClose ? (
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        if (this.state.notification?.onClose) {
                                            this.state.notification.onClose();
                                        }
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            ) : undefined
                        }
                    >
                        {this.state.notification?.message}
                    </MuiAlert>
                </UICollapse>
                <Wrapper>{this.RenderBuilder()}</Wrapper>

                <UILayerFormHost />

                <OpenAIChatLayer
                    title={"Chat with AI"}
                    onClose={() => this.setState({ chatOpen: false })}
                    open={this.state.chatOpen}
                    onSelectAnswer={() => {
                        // set chatOpen state to false
                        this.setState({ chatOpen: false });
                    }}
                />
            </TestAppRender>
        );
    }

    @AutoBind
    renderHomePage() {
        let titles: JSX.Element[] = [];
        let i = 0;

        while (i < this.menuItems.length) {
            let menuColumn = this.menuItems[i];

            let columnsSpan = this.menuItems.filter((m) => m.name === menuColumn.name).length;

            i += columnsSpan;

            titles.push(
                <TestHomeColumnTitle
                    color={menuColumn.color}
                    columnSpan={columnsSpan}
                >
                    {menuColumn.icon()} {menuColumn.name}
                </TestHomeColumnTitle>,
            );
        }

        return (
            <HomepageWrapper>
                <HompageBanner>
                    <Typography variant="h1">@appglue/rule-engine Demo App</Typography>
                    <Typography>Click open to explore each of the AppGlue Builders</Typography>
                </HompageBanner>
                <HomepageMainContent>
                    <TestHomeRender columnsCount={this.menuItems.length}>
                        <EmptyColumn />
                        {titles}
                        <EmptyColumn />
                        <EmptyColumn />
                        {this.menuItems.map((menuColumn, idx) => (
                            <TestHomeColumnUI
                                menuColumn={menuColumn}
                                key={idx}
                            />
                        ))}
                        <EmptyColumn />
                    </TestHomeRender>
                </HomepageMainContent>
            </HomepageWrapper>
        );
    }

    @AutoBind
    RenderBuilder() {
        let category = this.getCategory();

        // homepage
        if (category === "" && this.getPage() === "") {
            return this.renderHomePage();
        }

        for (let col of this.menuItems) {
            for (let cat of col.categories) {
                if (cat instanceof MenuCategory) {
                    for (let page of cat.items) {
                        if (cat.name === category && page.name === this.getPage()) {
                            return page.ui ? page.ui() : null;
                        }
                    }
                } else {
                    if (col.name === category && cat.name === this.getPage()) {
                        return cat.ui ? cat.ui() : null;
                    }
                }
            }
        }

        return null;
    }
}

export default App;

const Wrapper = styled.div`
    flex: 1;
    flex-flow: column;
    display: flex;

    overflow: auto;
`;

const FormWrapper = styled.div`
    border-width: 50px;
    height: 100vh;
    width: 50vw;
    flex-flow: column;
    display: flex;
`;

const HomepageWrapper = styled.div`
    background: whitesmoke;

    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const HompageBanner = styled.div`
    background: url("${HomePageTitleBg}");

    padding: 48px 32px;

    background-position: top right;
    background-size: auto 143px;
    background-repeat: no-repeat;

    .MuiTypography-root {
        &.MuiTypography-h1 {
            //styleName: title/h0 Inter ExtraBold 40px;

            font-size: 40px;
            font-style: normal;
            font-weight: 800;
            line-height: 48px;
            letter-spacing: 0em;
            text-align: left;

            margin-bottom: 4px;
            color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        }

        &.MuiTypography-body1 {
            //styleName: title/h3 Inter SemiBold 16px;

            font-size: 16px;
            font-style: normal;
            font-weight: 600;
            line-height: 22px;
            letter-spacing: 0em;
            text-align: left;
            color: ${UIStyleConstants.BLUE_COLOR};
        }
    }
`;

const HomepageMainContent = styled.div`
    overflow: auto;
`;

const TestHomeColumn = styled.div`
    width: 326px;

    display: flex;
    flex-direction: column;

    > div {
        margin-bottom: 8px;
    }
`;

const TestHomeColumnTitle = styled.div<{ color: string; columnSpan: number }>`
    grid-column: span ${(props) => props.columnSpan};

    border-bottom: solid 3px ${(props) => props.color};

    //styleName: title/h3 Inter SemiBold 16px;

    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: left;

    color: ${(props) => props.color};

    display: flex;
    align-items: center;

    padding: 8px 0;

    & > *:first-child {
        margin-right: 12px;
    }
`;

const TestHomeColumnDescription = styled.div<{ color: string }>`
    padding: 12px;
    //styleName: buttons / Inter Medium 12px;

    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 17px;
    letter-spacing: 0em;
    text-align: left;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    position: relative;

    border-radius: 8px;

    overflow: hidden;

    &::before {
        content: "";
        display: block;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;

        background: ${(props) => props.color};
        opacity: 0.1;
    }
`;

const TestHomeItemRender = styled.div`
    border: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
    border-radius: 8px;
    background: ${UIStyleConstants.WHITE_COLOR};
    width: 100%;
    padding: 20px;

    display: flex;
    flex-direction: column;
`;

const TestHomeItemImageWrapper = styled.div`
    position: relative;
    padding-top: 202px;
    overflow: hidden;

    margin-bottom: 16px;

    img {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 100%;
        transform: translateX(-50%) translateY(-50%);
    }
`;

const TestHomeItemActions = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    //styleName: title/h3 Inter SemiBold 16px;

    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: left;

    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
    align-items: center;
`;

const TestHomeRender = styled.div<{ columnsCount: number }>`
    display: grid;
    grid-template-columns: repeat(${(props) => props.columnsCount + 2}, max-content);

    margin-bottom: 40px;

    column-gap: 20px;

    row-gap: 8px;
`;

const TestHomeSectionHeader = styled.div`
    display: flex;
    flex-direction: column;
`;

const TestHomeSectionTitle = styled(Typography)`
    && {
        //styleName: title/h2 Inter Bold 24px;

        font-size: 24px;
        font-style: normal;
        font-weight: 700;
        line-height: 29px;
        letter-spacing: 0em;
        text-align: left;

        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

        margin-bottom: 8px;
    }
`;

const TestHomeDescription = styled(Typography)`
  && {
    //styleName: text/Inter Regular 12px;

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;
    color ${UIStyleConstants.GRAY_COLOR};;

    margin-bottom: 20px;
  }
`;

const EmptyColumn = styled.div`
    width: 12px;
`;

const TestHomeCategoryItemSection = styled.div`
    display: flex;
    flex-direction: column;

    padding-left: 16px;
    border-left: solid 4px ${UIStyleConstants.GRAY_LIGHTER_COLOR};

    .image-wrapper {
        padding-top: 163px;
    }

    .TestHomeItem {
        margin-top: 8px;

        &:first-child {
            margin-top: 0px;
        }
    }
`;

const TestAppRender = styled.div`
    height: 100vh;

    display: flex;
    flex-direction: column;
    overflow: hidden;
`;
